import { useState, useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import axios from "axios";
import { ChevronRight, Calendar, User, ArrowLeft, Phone } from "lucide-react";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import LeadForm from "@/components/common/LeadForm";
import { cityConfig } from "@/config/cityConfig";
import { useSchema } from "@/hooks/useMeta";
import { useSettings } from "@/hooks/useSettings";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ArticleSchema({ post }) {
  const schema = post ? {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.excerpt || "",
    image: post.image_url || "",
    datePublished: post.created_at,
    dateModified: post.updated_at || post.created_at,
    author: { "@type": "Person", name: post.author },
    publisher: {
      "@type": "Organization",
      name: "BRIMO Immobilien NRW",
      logo: { "@type": "ImageObject", url: cityConfig.manager.photo },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `https://www.brimo-immobilien-kreismettmann.de/aktuelles/${post.slug}`,
    },
  } : null;
  useSchema(schema);
  return null;
}

export default function BlogArticlePage() {
  const { slug } = useParams();
  const settings = useSettings();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    axios.get(`${API}/blog/${slug}`)
      .then((r) => {
        setPost(r.data);
        document.title = `${r.data.title} | BRIMO Immobilien NRW`;
      })
      .catch(() => setPost(null))
      .finally(() => setLoading(false));
  }, [slug]);

  if (settings.aktuelles_enabled === false) return <Navigate to="/" replace />;

  if (loading) return <div className="min-h-screen bg-[#1a1a1a]"><Navbar /><p className="text-gray-500 text-sm pt-32 text-center">Laden...</p></div>;
  if (!post) return <div className="min-h-screen bg-[#1a1a1a]"><Navbar /><p className="text-gray-500 text-sm pt-32 text-center">Beitrag nicht gefunden.</p><Footer /></div>;

  const date = new Date(post.created_at).toLocaleDateString("de-DE", { day: "numeric", month: "long", year: "numeric" });

  return (
    <div className="min-h-screen bg-[#1a1a1a]" data-testid="blog-article-page">
      <Navbar />
      <ArticleSchema post={post} />

      {post.image_url && (
        <section className="relative min-h-[40vh] flex items-end">
          <div className="absolute inset-0">
            <img src={post.image_url} alt={post.title} className="w-full h-full object-cover" loading="eager" />
            <div className="absolute inset-0 bg-[#0d0d0d]/90" />
            <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#1a1a1a] to-transparent" />
          </div>
          <div className="relative z-10 container mx-auto px-4 md:px-6 max-w-7xl pb-10 pt-32">
            <nav className="flex items-center gap-2 text-xs text-gray-500 mb-6" data-testid="breadcrumb">
              <Link to="/" className="hover:text-white transition-colors">Start</Link>
              <ChevronRight size={10} />
              <Link to="/aktuelles" className="hover:text-white transition-colors">Aktuelles</Link>
              <ChevronRight size={10} />
              <span className="text-white truncate max-w-xs">{post.title}</span>
            </nav>
            <h1
              className="text-3xl sm:text-4xl lg:text-5xl font-black text-white uppercase tracking-tight leading-tight max-w-3xl"
              style={{ fontFamily: "Montserrat, sans-serif" }}
              data-testid="blog-article-h1"
            >
              {post.title}
            </h1>
            <div className="flex items-center gap-4 mt-4 text-xs text-gray-400">
              {post.category && <span className="text-[#E61D38] font-bold uppercase tracking-wider">{post.category}</span>}
              <span className="flex items-center gap-1"><Calendar size={10} /> {date}</span>
              <span className="flex items-center gap-1"><User size={10} /> {post.author}</span>
            </div>
          </div>
        </section>
      )}

      {!post.image_url && (
        <section className="pt-32 pb-4">
          <div className="container mx-auto px-4 md:px-6 max-w-7xl">
            <nav className="flex items-center gap-2 text-xs text-gray-500 mb-6" data-testid="breadcrumb">
              <Link to="/" className="hover:text-white transition-colors">Start</Link>
              <ChevronRight size={10} />
              <Link to="/aktuelles" className="hover:text-white transition-colors">Aktuelles</Link>
              <ChevronRight size={10} />
              <span className="text-white">{post.title}</span>
            </nav>
            <div className="h-0.5 w-10 bg-[#E61D38] mb-4" />
            <h1
              className="text-3xl sm:text-4xl font-black text-white uppercase tracking-tight leading-tight mb-3 max-w-3xl"
              style={{ fontFamily: "Montserrat, sans-serif" }}
              data-testid="blog-article-h1"
            >
              {post.title}
            </h1>
            <div className="flex items-center gap-4 text-xs text-gray-400">
              {post.category && <span className="text-[#E61D38] font-bold uppercase tracking-wider">{post.category}</span>}
              <span className="flex items-center gap-1"><Calendar size={10} /> {date}</span>
              <span className="flex items-center gap-1"><User size={10} /> {post.author}</span>
            </div>
          </div>
        </section>
      )}

      <section className="py-12">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2">
              <div
                className="prose prose-invert prose-sm max-w-none
                  [&_h2]:text-white [&_h2]:font-bold [&_h2]:text-lg [&_h2]:mt-8 [&_h2]:mb-3 [&_h2]:font-[Montserrat,sans-serif]
                  [&_h3]:text-white [&_h3]:font-bold [&_h3]:text-base [&_h3]:mt-6 [&_h3]:mb-2
                  [&_p]:text-gray-400 [&_p]:leading-relaxed [&_p]:mb-4
                  [&_ul]:text-gray-400 [&_ul]:leading-relaxed [&_ul]:mb-4 [&_ul]:pl-5 [&_ul]:list-disc
                  [&_ol]:text-gray-400 [&_ol]:leading-relaxed [&_ol]:mb-4 [&_ol]:pl-5 [&_ol]:list-decimal
                  [&_li]:mb-1
                  [&_strong]:text-white
                  [&_a]:text-[#E61D38] [&_a]:hover:underline
                  [&_blockquote]:border-l-2 [&_blockquote]:border-[#E61D38] [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-gray-400
                  [&_img]:w-full [&_img]:my-6 [&_img]:border [&_img]:border-white/5"
                data-testid="blog-content"
                dangerouslySetInnerHTML={{ __html: post.content }}
              />
              <div className="mt-14">
                <LeadForm
                  formType="contact"
                  title="Fragen zu diesem Thema?"
                  ctaText="Nachricht senden"
                  pageSource={`/aktuelles/${post.slug}`}
                />
              </div>
            </div>
            <div className="lg:col-span-1">
              <Link to="/aktuelles" className="flex items-center gap-2 text-gray-400 text-xs uppercase tracking-wider mb-6 hover:text-white transition-colors">
                <ArrowLeft size={12} /> Alle Beiträge
              </Link>
              <div className="bg-[#0d0d0d] border border-white/5 p-6 mb-6" data-testid="blog-sidebar-contact">
                <div className="flex items-center gap-4 mb-4">
                  <img
                    src={cityConfig.manager.photo}
                    alt={cityConfig.manager.name}
                    className="w-14 h-14 rounded-full object-cover border-2 border-[#E61D38]"
                    loading="lazy"
                  />
                  <div>
                    <p className="text-white text-sm font-bold">{cityConfig.manager.name}</p>
                    <p className="text-gray-500 text-[10px] uppercase tracking-wider">{cityConfig.manager.title}</p>
                  </div>
                </div>
                <p className="text-gray-400 text-xs leading-relaxed mb-4">
                  Haben Sie Fragen zu diesem Beitrag oder benötigen persönliche Beratung?
                </p>
                <a href={`tel:${cityConfig.phone}`} className="flex items-center gap-2 text-[#E61D38] text-sm font-bold hover:underline">
                  <Phone size={14} /> {cityConfig.phoneDisplay}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
